import {
	Component,
	EventEmitter,
	Inject,
	Input,
	OnInit,
	Output,
} from '@angular/core';
import {
	AbstractControl,
	FormArray,
	FormBuilder,
	FormGroup,
	Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { openClose } from '@core/animations/animations';
import { ICON } from '@core/util/icon';
import { getFullName } from '@core/util/string';
import { BusinessMatterClientsRelationships } from '@models/businessMatterClientsRelationships';
import { TreefortVerificationSettings } from '@models/businessSettings/settings';
import { PersonClient } from '@models/clients';
import { IDVModalSteps } from '@models/idv';
import { MatterBusinessUser } from '@models/matter';
import { UserProfile } from '@models/userProfile';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import {
	IdvService,
	Profile,
	VerificationRequest,
} from '@services/idv/idv.service';
import { SnackbarService } from '@services/ui/snackbar.service';
import { BusinessState } from '@state/business';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { UserProfileState } from '@state/user-profile';
import { Observable } from 'rxjs';
import { duplicatesInFormArray } from 'src/app/shared/validators/same-value.validator';
import {
	IdvRequestModalComponent,
	IdvRequestModalData,
	IdvSendRequestData,
} from '../../idv-modal.component';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { TestHooks } from './send-requests.hooks';

type ITreefortProfile<T = Profile> = {
	[K in keyof T]: AbstractControl<T[K]>;
};

@Component({
	selector: 'app-send-requests',
	templateUrl: './send-requests.component.html',
	styleUrls: ['./send-requests.component.scss'],
	animations: [openClose()],
	standalone: false,
})
@UntilDestroy()
export class SendRequestsComponent implements OnInit {
	testHooks = TestHooks;
	@ViewSelectSnapshot(BusinessState.getTreefortConnected)
	public isTreefortConnected: boolean;
	@SelectSnapshot(UserProfileState.getCurrentBusinessId)
	public businessId: string;
	@SelectSnapshot(UserProfileState.getProfile) loggedInUser: UserProfile;
	// Only works from inside a matter
	@SelectSnapshot(SelectedMatter.assignedLawyer)
	public lawyer: MatterBusinessUser;
	@SelectSnapshot(SelectedMatter.id) public matterId: string;
	@SelectSnapshot(SelectedMatter.matterName) public matterName: string;
	@SelectSnapshot(SelectedMatter.matterNumber) public matterNumber: string;
	// Only works from inside a matter
	@Select(BusinessState.getTreefortVerificationSettings)
	public $treefortVerificationSettings: Observable<TreefortVerificationSettings>;
	@SelectSnapshot(BusinessState.businessName) public businessName: string;
	@Input() set selectedClients(clients: PersonClient[]) {
		this.mapToProfile(
			clients.map((client) => ({
				email: client.idv?.email ?? client.email,
				phoneNumber: client.idv?.phoneNumber ?? client.phone,
				clientId: client.id,
				clientRequestId: client.idv?.clientRequestId,
				name: getFullName([
					client.firstName,
					client.middleNames,
					client.lastName,
				]),
				matterId: client.idv?.matterId,
				matterNumber: client.idv?.matterNumber,
				matterName: client.idv?.matterName,
			})),
		);
		this.selectedClientCount = clients.length;
	}

	@Input() public hasCheckboxes = true;
	@Input() public requestDataFromTable: BusinessMatterClientsRelationships[] =
		[];

	@Output() public sendRequestEvent = new EventEmitter<IDVModalSteps>();

	public selectedClientCount = 0;
	public isSending = false;
	public ICON = ICON;
	public idvModalSteps = IDVModalSteps;
	public idvModalStep: string = IDVModalSteps.SEND_REQUESTS;
	public isExpanded = false;
	public expandedAriaLabel = 'Expand Treefort settings';
	public collapsedAriaLabel = 'Collapse Treefort settings';
	public clientsWithIdvs: PersonClient[] = [];
	private standaloneModal = false;

	public profileForm = this.formBuilder.group({
		profiles: this.formBuilder.array<FormGroup<ITreefortProfile>>([], {
			validators: duplicatesInFormArray('Email'),
			updateOn: 'change',
		}),
		secondaryIdRequired: this.formBuilder.control(false),
		financialInstitutionLoginRequired: this.formBuilder.control(false),
		advancedAMLScreeningRequired: this.formBuilder.control(false),
	});

	constructor(
		private idvService: IdvService,
		public dialogRef: MatDialogRef<IdvRequestModalComponent>,
		private snackbarService: SnackbarService,
		private analytics: AnalyticsService,
		@Inject(MAT_DIALOG_DATA) public data: IdvRequestModalData,
		private formBuilder: FormBuilder,
	) {}

	ngOnInit(): void {
		if (this.data?.idvSendRequestData?.length) {
			this.mapToProfile(this.data.idvSendRequestData);
		}
		this.standaloneModal = this.data.standaloneModal;
		this.$treefortVerificationSettings
			.pipe(untilDestroyed(this))
			.subscribe((treefortVerificationSettings) => {
				this.profileForm.patchValue(treefortVerificationSettings);
			});
	}

	get profiles() {
		return this.profileForm.get('profiles') as FormArray;
	}

	onSubmit(): void {
		if (this.profileForm.status === 'INVALID') {
			return;
		}

		this.isSending = true;

		this.analytics.logEvent(AnalyticsEvent.TREEFORT_SEND_REQUEST);

		this.sendIDVRequest()
			.pipe(untilDestroyed(this))
			.subscribe(
				() => {
					this.isSending = false;
					this.closeModal();
					this.snackbarService.openSnackBar({
						text: this.getSuccessToastMessage(),
						type: 'success',
						icon: ICON.CIRCLE_CHECK,
					});
				},
				(error) => {
					console.error(error);
					this.isSending = false;
					this.closeModal();
					this.snackbarService.openSnackBar({
						text: this.getErrorToastMessage(),
						type: 'error',
						icon: ICON.EXCLAMATION,
					});
				},
			);
	}

	onBackHandler() {
		this.standaloneModal
			? this.closeModal()
			: this.setModalStep(IDVModalSteps.CLIENTS_SELECT);
	}

	toggleTreefortSettingsExpansion(event: MouseEvent): void {
		event.preventDefault();
		this.isExpanded = !this.isExpanded;
	}

	private setModalStep(step: IDVModalSteps): void {
		this.sendRequestEvent.emit(step);
	}

	private sendIDVRequest() {
		const verificationRequest: VerificationRequest = {
			InitiatorName: [
				this.loggedInUser?.firstName,
				this?.loggedInUser?.lastName,
			]
				.filter(Boolean)
				.join(' '),
			InitiatorEmail: this.loggedInUser?.email,
			InitiatorOrganizationName: this.businessName,
			IsSecondaryIdRequired: this.profileForm.get('secondaryIdRequired').value,
			IsBankingVerificationRequired: this.profileForm.get(
				'financialInstitutionLoginRequired',
			).value,
			IsAdvancedAMLScreeningRequired: this.profileForm.get(
				'advancedAMLScreeningRequired',
			).value,
			JurisdictionCode: 'LSO',
			Profiles: this.profiles.getRawValue(),
			RequesterReferenceId: this.businessId,
			RequestedById: this.loggedInUser?.id,
		};

		return this.idvService.upsertIDVRequest(verificationRequest);
	}

	private closeModal(): void {
		this.dialogRef.close();
	}

	private getSuccessToastMessage(): string {
		return `Identity verification${this.selectedClientCount > 1 ? ' requests have ' : ' request has '}been sent!`;
	}

	private getErrorToastMessage(): string {
		return `Identity verification${this.selectedClientCount > 1 ? ' requests have ' : ' request has '}failed. Please try again.`;
	}

	private mapToProfile(clientInfos: IdvSendRequestData[]) {
		for (const clientInfo of clientInfos) {
			this.profiles.push(
				this.formBuilder.group<ITreefortProfile>({
					Email: this.formBuilder.control(clientInfo.email || '', {
						validators: [Validators.required, Validators.email],
						// asyncValidators: treefortEmailValidator(clientInfo.email, ![undefined, null].includes(clientInfo.clientRequestId), this.idvService),
					}),
					DialCode: this.formBuilder.control(clientInfo.dialCode || ''),
					PhoneNumber: this.formBuilder.control(
						clientInfo.phoneNumber || '',
						Validators.required,
					),
					RequesterClientRequestId: this.formBuilder.control({
						value: clientInfo.clientId,
						disabled: true,
					}),
					ClientRequestId: this.formBuilder.control({
						value: clientInfo.clientRequestId,
						disabled: true,
					}),
					Name: this.formBuilder.control({
						value: clientInfo.name,
						disabled: true,
					}),
					MatterId: this.formBuilder.control({
						value: this.matterId ?? clientInfo.matterId,
						disabled: true,
					}),
					MatterNumber: this.formBuilder.control({
						value: this.matterNumber ?? clientInfo.matterNumber,
						disabled: true,
					}),
					MatterName: this.formBuilder.control({
						value: this.matterName ?? clientInfo.matterName,
						disabled: true,
					}),
					LawyerId: this.formBuilder.control({
						value: this.lawyer?.id ?? clientInfo.lawyerName,
						disabled: true,
					}),
					LawyerName: this.formBuilder.control({
						value: this.lawyer?.name ?? clientInfo.lawyerId,
						disabled: true,
					}),
				}),
			);
		}
	}
}
